import * as React from 'react';
import styled from 'styled-components';
import { Color } from 'csstype';
import { $switchCaseStrict } from 'dolla-switch';
import { WrappingAnchor } from './WrappingAnchor';
import { getTheme, ThemeName, disabledTheme } from './themes';

export type ButtonSize = 'small' | 'medium' | 'large';

export interface ButtonTheme {
    fgColor: Color;
    bgColor: Color;
    bgHoverColor: Color;
    borderColor?: Color;
    borderHoverColor?: Color;
}

export type BaseButtonProps = React.HTMLAttributes<HTMLAnchorElement> & {
    id?: string;
    href?: string;
    target?: string;
    ref?: React.RefObject<HTMLAnchorElement>;
    onClick?: React.MouseEventHandler;
    fontSize?: number;
    theme?: ButtonTheme | ThemeName;
    border?: boolean;
    isActive?: boolean;
    isDisabled?: boolean;
    isFullWidth?: boolean;
    dataLookup?: string;
    className?: string;
};

export type ButtonProps = BaseButtonProps & {
    size?: ButtonSize;
    centreChildren?: boolean;
};

const ChildrenContainer = styled.span<{
    size: ButtonSize;
    centreChildren: boolean;
}>`
    flex: 0 1 auto;
    line-height: 18px;
    margin: 7px 8px;
    user-select: none;

    ${props =>
        $switchCaseStrict(props.size, {
            small: () => `font-size: 12px;`,
            medium: () => `font-size: 14px;`,
            large: () => `font-size: 16px;`,
        })};
    ${({ centreChildren }) =>
        centreChildren
            ? `
    display: flex;
    align-items: center;
    justify-content: center;`
            : ''};
`;

export const Button: React.FunctionComponent<ButtonProps> = ({
    target = '_self',
    ref,
    children,
    id,
    href,
    isActive,
    isDisabled = false,
    onClick,
    size = 'medium',
    theme = 'light',
    border = false,
    isFullWidth = false,
    centreChildren = false,
    dataLookup,
    className,
    ...props
}) => {
    const btnTheme = typeof theme === 'string' ? getTheme(theme as ThemeName) : theme;

    return (
        <WrappingAnchor
            id={id}
            ref={ref}
            size={size}
            href={href}
            theme={isDisabled ? disabledTheme : btnTheme}
            border={border}
            target={href ? target : undefined}
            isActive={isActive}
            disabled={isDisabled}
            onClick={e => !isDisabled && onClick && onClick(e)}
            isFullWidth={isFullWidth}
            data-lookup={dataLookup || id}
            className={className}
            {...props}
        >
            <ChildrenContainer size={size} centreChildren={centreChildren}>
                {children}
            </ChildrenContainer>
        </WrappingAnchor>
    );
};
